import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { colors } from "../../styles/colors";
import type { NoteLocation } from "./noteTypes";

const pinIcon = L.divIcon({
  className: "note-map-pin",
  html: `<svg width="28" height="28" viewBox="0 0 24 24" fill="${colors.brandPrimary}" stroke="white" stroke-width="1.5" stroke-linejoin="round"><path d="M12 21s-7-6.2-7-11a7 7 0 0 1 14 0c0 4.8-7 11-7 11z"/><circle cx="12" cy="10" r="2.6" fill="white" stroke="none"/></svg>`,
  iconSize: [28, 28],
  iconAnchor: [14, 28],
});

/** Read-only mini map showing a single marker. */
export function NoteMapView({ location, height = 160 }: { location: NoteLocation; height?: number }) {
  return (
    <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${colors.borderLight}` }}>
      <MapContainer
        center={[location.lat, location.lng]}
        zoom={14}
        style={{ height, width: "100%" }}
        scrollWheelZoom={false}
        dragging={true}
        attributionControl={false}
      >
        <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <Marker position={[location.lat, location.lng]} icon={pinIcon} />
      </MapContainer>
    </div>
  );
}

function ClickCapture({ onPick }: { onPick: (lat: number, lng: number) => void }) {
  useMapEvents({
    click(e) {
      onPick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

function Recenter({ lat, lng }: { lat: number; lng: number }) {
  const map = useMap();
  useEffect(() => {
    map.setView([lat, lng], map.getZoom());
  }, [lat, lng, map]);
  return null;
}

interface GeoResult {
  lat: string;
  lon: string;
  display_name: string;
}

/** Interactive picker: search (Nominatim) + click-to-place marker. */
export function LocationPicker({
  value,
  onChange,
}: {
  value: NoteLocation | null;
  onChange: (loc: NoteLocation) => void;
}) {
  const [query, setQuery] = useState(value?.label || "");
  const [results, setResults] = useState<GeoResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const center: [number, number] = value ? [value.lat, value.lng] : [20, 0];
  const zoom = value ? 14 : 2;

  const runSearch = async (q: string) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(q)}`,
        { headers: { "Accept-Language": "en" } }
      );
      const data = (await res.json()) as GeoResult[];
      setResults(Array.isArray(data) ? data : []);
      setShowResults(true);
    } catch {
      setResults([]);
    } finally {
      setSearching(false);
    }
  };

  const onQueryChange = (v: string) => {
    setQuery(v);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => runSearch(v), 450);
  };

  const selectResult = (r: GeoResult) => {
    const lat = parseFloat(r.lat);
    const lng = parseFloat(r.lon);
    setQuery(r.display_name);
    setShowResults(false);
    onChange({ lat, lng, label: r.display_name });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ position: "relative" }}>
        <input
          value={query}
          onChange={(e) => onQueryChange(e.target.value)}
          onFocus={() => results.length > 0 && setShowResults(true)}
          placeholder="Search a place or address…"
          style={{
            width: "100%",
            boxSizing: "border-box",
            border: `1px solid ${colors.borderLight}`,
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 13,
            outline: "none",
            fontFamily: "inherit",
          }}
        />
        {searching && (
          <span style={{ position: "absolute", right: 10, top: 9, fontSize: 11, color: colors.textTertiary }}>…</span>
        )}
        {showResults && results.length > 0 && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 4px)",
              left: 0,
              right: 0,
              zIndex: 1000,
              background: "#fff",
              border: `1px solid ${colors.borderLight}`,
              borderRadius: 8,
              boxShadow: "0 6px 20px rgba(0,0,0,0.12)",
              overflow: "hidden",
            }}
          >
            {results.map((r, i) => (
              <button
                key={`${r.lat}-${r.lon}-${i}`}
                type="button"
                onClick={() => selectResult(r)}
                style={{
                  display: "block",
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 12px",
                  fontSize: 12,
                  color: colors.textSecondary,
                  background: "transparent",
                  border: "none",
                  borderBottom: i < results.length - 1 ? `1px solid ${colors.borderLight}` : "none",
                  cursor: "pointer",
                }}
              >
                {r.display_name}
              </button>
            ))}
          </div>
        )}
      </div>

      <div style={{ borderRadius: 10, overflow: "hidden", border: `1px solid ${colors.borderLight}` }}>
        <MapContainer center={center} zoom={zoom} style={{ height: 200, width: "100%" }} attributionControl={false}>
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
          <ClickCapture
            onPick={(lat, lng) => onChange({ lat, lng, label: value?.label && query === value.label ? value.label : query || undefined })}
          />
          {value && <Marker position={[value.lat, value.lng]} icon={pinIcon} />}
          {value && <Recenter lat={value.lat} lng={value.lng} />}
        </MapContainer>
      </div>
      <span style={{ fontSize: 11, color: colors.textTertiary }}>
        Search above or click the map to drop a pin.
      </span>
    </div>
  );
}
